import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { TrendingDown, TrendingUp } from "lucide-react";
import { api } from "../lib/api";

const SPINE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/a421fc4c-04c6-4c3c-9709-60b8a409d32c/images/fdc49169dc518fb39749737b4195ff1296bce19029ddf6e2ec410041dbf016f8.png";

/* Build a smooth area-chart path from up to 7 recent pain scores (0–10). */
function buildChart(values, w, h) {
  if (values.length < 2) return null;
  const max = 10;
  const stepX = w / (values.length - 1);
  const pts = values.map((v, i) => [i * stepX, h - (Math.max(0, Math.min(max, v)) / max) * h]);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  return { line, area, pts };
}

export default function PainTrackerCard() {
  const [history, setHistory] = useState(null);

  useEffect(() => {
    api.get("/daily-checkin").then((r) => setHistory(r.data || [])).catch(() => setHistory([]));
  }, []);

  const recent = (history || []).filter((c) => typeof c.pain_score === "number").slice(-7);
  const score = recent.length ? recent[recent.length - 1].pain_score : null;
  const prev = recent.length > 1 ? recent[recent.length - 2].pain_score : null;
  const delta = score != null && prev != null ? score - prev : null;

  const W = 200, H = 88;
  const chart = buildChart(recent.map((c) => c.pain_score), W, H);

  // Severity color: low pain = green, moderate = amber, high = red.
  const sev = score == null ? "#FFFFFF" : score <= 3 ? "#10B981" : score <= 6 ? "#F59E0B" : "#EF4444";
  const sevLabel = score == null ? "" : score <= 3 ? "Low" : score <= 6 ? "Moderate" : "High";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      data-testid="pain-tracker-card"
      className="relative overflow-hidden rounded-3xl border border-[#22D3EE]/25 p-5"
      style={{
        background:
          "radial-gradient(120% 90% at 0% 0%, rgba(34,211,238,0.18), transparent 55%), radial-gradient(110% 90% at 100% 100%, rgba(245,158,11,0.12), transparent 55%), linear-gradient(160deg, rgba(10,21,48,0.92) 0%, rgba(3,16,31,0.96) 100%)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="bps-section-head text-[#22D3EE]" style={{ textShadow: "0 0 14px rgba(34,211,238,0.55)" }}>Pain Tracker</div>
        <Link to="/progress" data-testid="pain-tracker-history" className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8] hover:text-[#22D3EE] transition-colors">
          7-day trend
        </Link>
      </div>

      <div className="flex items-center gap-4">
        {/* Glowing spine visual */}
        <div className="relative w-24 sm:w-28 shrink-0">
          <div className="absolute inset-0 rounded-2xl blur-2xl" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.35), transparent 70%)" }} />
          <img src={SPINE_IMG} alt="Spine pain map" className="relative w-full h-auto object-contain drop-shadow-[0_0_18px_rgba(34,211,238,0.4)]" draggable={false} />
        </div>

        {/* Score + chart */}
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7C8FA8]">Pain score</div>
          <div className="flex items-end gap-2 mt-0.5">
            <div className="font-display text-5xl font-light leading-none transition-colors" style={{ color: sev, textShadow: score != null ? `0 0 22px ${sev}66` : "none" }} data-testid="pain-score-value">
              {score != null ? score.toFixed(1) : "—"}
            </div>
            {delta != null && delta !== 0 && (
              <span className={`inline-flex items-center gap-0.5 text-xs font-semibold mb-1.5 ${delta < 0 ? "text-[#10B981]" : "text-[#F59E0B]"}`}>
                {delta < 0 ? <TrendingDown size={13} /> : <TrendingUp size={13} />}
                {Math.abs(delta).toFixed(1)}
              </span>
            )}
            {sevLabel && (
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1.5 px-2 py-0.5 rounded-full" style={{ color: sev, background: `${sev}1f`, border: `1px solid ${sev}55` }} data-testid="pain-severity-label">
                {sevLabel}
              </span>
            )}
          </div>

          <div className="mt-3 -mb-1">
            {chart ? (
              <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[72px]" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="pt-area" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <motion.path d={chart.area} fill="url(#pt-area)"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} />
                <motion.path d={chart.line} fill="none" stroke="#22D3EE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ filter: "drop-shadow(0 0 5px rgba(34,211,238,0.7))" }}
                  initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1, ease: "easeOut" }} />
                {chart.pts.length > 0 && (
                  <circle cx={chart.pts[chart.pts.length - 1][0]} cy={chart.pts[chart.pts.length - 1][1]} r="3.5" fill="#22D3EE" style={{ filter: "drop-shadow(0 0 6px #22D3EE)" }} />
                )}
              </svg>
            ) : (
              <p className="text-xs text-[#7C8FA8] leading-relaxed">Log a daily check-in to start tracking your pain trend.</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
